import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { CheckCircle, XCircle, Clock, MapPin, Delete, LogIn, ShieldAlert } from 'lucide-react';

const BACKEND_URL = window.location.origin;

export default function Kiosk() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('pending');

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchLocations();
    getUserLocation();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/locations`);
      const active = response.data.filter(loc => loc.is_active);
      setLocations(active);
      if (active.length > 0) {
        setSelectedLocation(active[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      setLocationStatus('loading');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationStatus('granted');
        },
        () => {
          setLocationStatus('denied');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationStatus('unavailable');
    }
  };

  const handleKeyPress = (digit) => {
    if (employeeCode.length < 6) {
      setEmployeeCode(prev => prev + digit);
    }
  };

  const handleBackspace = () => {
    setEmployeeCode(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    setEmployeeCode('');
  };

  const handleSubmit = async () => {
    if (employeeCode.length !== 6 || isSubmitting) return;

    setIsSubmitting(true);
    setResult(null);

    const getLatestPosition = () => new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve(userLocation),
          { enableHighAccuracy: true, timeout: 5000 }
        );
      } else {
        resolve(userLocation);
      }
    });

    try {
      const currentPos = await getLatestPosition();

      const payload = {
        employee_code: employeeCode,
        location_id: selectedLocation || null,
        latitude: currentPos?.latitude || null,
        longitude: currentPos?.longitude || null,
      };

      const response = await axios.post(`${BACKEND_URL}/api/attendance/capture`, payload);

      setResult({ success: true, data: response.data });

      const outOfRange = response.data.out_of_range;
      if (outOfRange) {
        toast.warning(
          `${response.data.employee_name} is ${response.data.geofence_distance}m away from branch. Attendance flagged.`
        );
      } else {
        toast.success(
          response.data.action === 'check_in'
            ? `Welcome, ${response.data.employee_name}! Checked in at ${response.data.time}`
            : `Goodbye, ${response.data.employee_name}! Worked ${response.data.hours_worked}`
        );
      }

      setTimeout(() => {
        setResult(null);
        setEmployeeCode('');
      }, 4000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      setResult({ success: false, error: message });
      toast.error(message);

      setTimeout(() => {
        setResult(null);
        setEmployeeCode('');
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (result) return;
      if (e.key >= '0' && e.key <= '9') {
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter' && employeeCode.length === 6) {
        handleSubmit();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-start sm:justify-center px-4 py-4 sm:p-8 select-none overflow-y-auto" data-testid="kiosk-page">
      <Toaster position="top-center" richColors />

      {/* Header — compact on mobile */}
      <div className="text-center mb-2 sm:mb-6">
        <div className="flex items-center justify-center mb-1 sm:mb-3">
          <img
            src="/seeza-logo.png"
            alt="Seeza Trading Company"
            className="h-8 sm:h-14 w-auto object-contain bg-white rounded-xl p-1 sm:p-2"
          />
        </div>
        <div className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-0.5" data-testid="kiosk-time">
          {formatTime(currentTime)}
        </div>
        <p className="text-xs sm:text-sm text-slate-400">{formatDate(currentTime)}</p>
      </div>

      {/* Result Overlay */}
      {result && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${
          result.success
            ? result.data?.out_of_range
              ? 'bg-yellow-600/90'
              : 'bg-[#138808]/90'
            : 'bg-red-600/90'
        }`} data-testid="kiosk-result-overlay">
          <div className="text-white text-center px-6 animate-fade-in">
            {result.success ? (
              <>
                {result.data?.out_of_range ? (
                  <ShieldAlert className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3" />
                ) : (
                  <CheckCircle className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3" />
                )}
                <p className="text-2xl sm:text-3xl font-bold mb-2">{result.data.employee_name}</p>
                <p className="text-lg sm:text-xl mb-1">
                  {result.data.action === 'check_in' ? 'Checked In' : 'Checked Out'}
                </p>
                <p className="text-3xl sm:text-4xl font-bold mt-2">{result.data.time}</p>
                {result.data.action === 'check_out' && result.data.hours_worked && (
                  <p className="mt-2 sm:mt-3 text-base sm:text-lg">Worked: {result.data.hours_worked}</p>
                )}
                {result.data.is_late && (
                  <p className="mt-2 text-yellow-200">Late by {result.data.late_by}</p>
                )}
                {result.data.out_of_range && (
                  <p className="mt-2 text-sm bg-white/20 rounded-lg px-3 py-1.5 inline-block">
                    Out of Range: {result.data.geofence_distance}m from branch
                  </p>
                )}
                <p className="mt-2 sm:mt-3 text-sm opacity-80">{result.data.location_name}</p>
              </>
            ) : (
              <>
                <XCircle className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3" />
                <p className="text-xl sm:text-2xl font-bold px-4">{result.error}</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Location Selector — above keypad on mobile for visibility */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-5 w-full max-w-xs sm:max-w-sm" data-testid="kiosk-location-bar">
        <MapPin className="w-4 h-4 text-slate-500 shrink-0" />
        <Select value={selectedLocation} onValueChange={setSelectedLocation}>
          <SelectTrigger className="flex-1 bg-slate-800 border-slate-600 text-white text-sm h-10 sm:h-9" data-testid="kiosk-location-select">
            <SelectValue placeholder="Select Branch" />
          </SelectTrigger>
          <SelectContent>
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5 shrink-0" data-testid="gps-status">
          <div className={`w-2.5 h-2.5 rounded-full ${
            locationStatus === 'granted' ? 'bg-green-400' :
            locationStatus === 'loading' ? 'bg-yellow-400 animate-pulse' :
            'bg-red-400'
          }`} />
          <span className="text-xs text-slate-500 hidden sm:inline">
            {locationStatus === 'granted' ? 'GPS Active' :
             locationStatus === 'loading' ? 'Getting GPS...' :
             locationStatus === 'denied' ? 'GPS Denied' : 'No GPS'}
          </span>
          {locationStatus === 'denied' && (
            <Button variant="ghost" size="sm" className="text-xs text-slate-400 h-auto p-0 ml-1" onClick={getUserLocation}>
              Retry
            </Button>
          )}
        </div>
      </div>

      {/* Code Display */}
      <div className="mb-2 sm:mb-5 w-full max-w-xs sm:max-w-sm" data-testid="kiosk-code-display">
        <p className="text-slate-400 text-center text-[10px] sm:text-sm mb-1.5 font-medium tracking-wide uppercase">Enter 6-Digit Employee Code</p>
        <div className="flex justify-center gap-1.5 sm:gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`w-11 h-12 sm:w-14 sm:h-16 rounded-lg border-2 flex items-center justify-center text-xl sm:text-3xl font-bold transition-all duration-150 ${
                employeeCode[i]
                  ? 'border-[#FF9933] bg-[#FF9933]/10 text-white'
                  : i === employeeCode.length
                  ? 'border-[#FF9933]/60 bg-slate-800 text-slate-500 animate-pulse'
                  : 'border-slate-600 bg-slate-800 text-slate-500'
              }`}
            >
              {employeeCode[i] || ''}
            </div>
          ))}
        </div>
      </div>

      {/* Numeric Keypad — larger touch targets */}
      <div className="w-full max-w-[280px] sm:max-w-xs mb-2 sm:mb-5" data-testid="kiosk-keypad">
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2.5">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
            <Button
              key={digit}
              onClick={() => handleKeyPress(digit)}
              disabled={employeeCode.length >= 6 || isSubmitting}
              className="h-12 sm:h-16 text-xl sm:text-2xl font-bold bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-white border border-slate-600 rounded-xl transition-all active:scale-95 touch-manipulation"
              data-testid={`kiosk-key-${digit}`}
            >
              {digit}
            </Button>
          ))}
          <Button
            onClick={handleClear}
            disabled={isSubmitting}
            className="h-12 sm:h-16 text-xs sm:text-sm font-semibold bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-red-400 border border-slate-600 rounded-xl touch-manipulation"
            data-testid="kiosk-key-clear"
          >
            Clear
          </Button>
          <Button
            onClick={() => handleKeyPress('0')}
            disabled={employeeCode.length >= 6 || isSubmitting}
            className="h-12 sm:h-16 text-xl sm:text-2xl font-bold bg-slate-700 hover:bg-slate-600 active:bg-slate-500 text-white border border-slate-600 rounded-xl transition-all active:scale-95 touch-manipulation"
            data-testid="kiosk-key-0"
          >
            0
          </Button>
          <Button
            onClick={handleBackspace}
            disabled={employeeCode.length === 0 || isSubmitting}
            className="h-12 sm:h-16 bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-slate-300 border border-slate-600 rounded-xl touch-manipulation"
            data-testid="kiosk-key-backspace"
          >
            <Delete className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={employeeCode.length !== 6 || isSubmitting}
        size="lg"
        className="w-full max-w-[280px] sm:max-w-xs h-12 sm:h-14 text-base sm:text-lg font-semibold bg-[#FF9933] hover:bg-[#E68A2E] disabled:bg-slate-700 disabled:text-slate-500 text-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-xl mb-4 touch-manipulation"
        data-testid="kiosk-submit-btn"
      >
        {isSubmitting ? (
          <span className="flex items-center gap-3">
            <Clock className="w-5 h-5 animate-spin" />
            Verifying...
          </span>
        ) : (
          <span className="flex items-center gap-3">
            <LogIn className="w-5 h-5" />
            Submit Attendance
          </span>
        )}
      </Button>

      {/* Instructions — hidden on very small screens */}
      <p className="text-slate-500 text-center text-xs sm:text-sm max-w-sm hidden sm:block">
        Enter your 6-digit Employee Code, then press "Submit Attendance" to check in or out.
      </p>

      {/* Indian Tricolor Bar */}
      <div className="mt-4 sm:mt-6 flex gap-0 rounded-full overflow-hidden shadow-lg">
        <div className="w-12 sm:w-16 h-1.5 sm:h-2 bg-[#FF9933]" />
        <div className="w-12 sm:w-16 h-1.5 sm:h-2 bg-white" />
        <div className="w-12 sm:w-16 h-1.5 sm:h-2 bg-[#138808]" />
      </div>
    </div>
  );
}
